import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { FileUploadButton } from "@/components/FileUploadButton";

const emptyForm = {
  name: "", destination: "", start_date: "", end_date: "",
  trip_type: "family", notes: "", cover_file_id: null,
};

export default function NewTripDialog({ trigger, open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();
  const { canCreateTrip, refresh: refreshPlan } = useBilling();

  function handleOpenChange(next) {
    if (next && !canCreateTrip) {
      toast.error("Free plan is limited to 1 trip — upgrade to add more.");
      nav("/pricing");
      return;
    }
    onOpenChange?.(next);
  }

  async function createTrip() {
    if (!form.name || !form.destination) {
      toast.error("Name and destination required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/trips", form);
      onCreated?.(data);
      onOpenChange?.(false);
      setForm(emptyForm);
      refreshPlan();
      toast.success("Trip created");
      nav(`/trips/${data.id}`);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(formatError(e));
        onOpenChange?.(false);
        nav("/pricing");
      } else {
        toast.error(formatError(e));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger ? (
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : null}
      <DialogContent className="rounded-2xl max-w-lg">
        <DialogHeader><DialogTitle className="font-display">New trip</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Trip name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="rounded-xl" placeholder="Summer in Portugal" data-testid="trip-name-input" />
          </div>
          <div>
            <Label>Destination</Label>
            <Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })}
                   className="rounded-xl" placeholder="Lisbon, Portugal" data-testid="trip-destination-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <Input type="text" inputMode="numeric" placeholder="YYYY-MM-DD" title="Format: YYYY-MM-DD"
                     value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                     className="rounded-xl" data-testid="trip-start-input" />
            </div>
            <div>
              <Label>End date</Label>
              <Input type="text" inputMode="numeric" placeholder="YYYY-MM-DD" title="Format: YYYY-MM-DD"
                     value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                     className="rounded-xl" data-testid="trip-end-input" />
            </div>
          </div>
          <div>
            <Label>Traveler type</Label>
            <Select value={form.trip_type} onValueChange={(v) => setForm({ ...form, trip_type: v })}>
              <SelectTrigger className="rounded-xl" data-testid="trip-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="family">Family</SelectItem>
                <SelectItem value="group">Group</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="rounded-xl" placeholder="Anything to remember..." />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <FileUploadButton
              label={form.cover_file_id ? "Change cover" : "Add cover photo"}
              testId="trip-cover-upload"
              onUploaded={(id) => setForm((f) => ({ ...f, cover_file_id: id }))}
            />
            {form.cover_file_id && <span className="text-xs text-stone-500">Cover attached</span>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} className="rounded-full">Cancel</Button>
          <Button onClick={createTrip} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-trip-btn">
            {saving ? "Creating..." : "Create trip"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const NewTripTrigger = (
  <Button
    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:-translate-y-0.5 transition-transform"
    data-testid="new-trip-btn"
  >
    <Plus className="w-4 h-4 mr-2" /> New trip
  </Button>
);
